import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  fetchAdminEvents,
  fetchAdminEventById,
  clearAuditNav,
} from "@/store/slices/adminSlice";
import {
  Search, Calendar, MapPin, Eye, MoreVertical,
  CheckCircle, Clock, Archive, ListChecks, Globe,
  Filter, ChevronDown, DollarSign, Package, EyeOff
} from "lucide-react";
import { SPONSORSHIP_COMPONENTS, getComponentLabel } from "@/utils/constants";
import EventFilters from "@/components/common/EventFilters";
import Badge from "@/components/common/Badge";
import Pagination from "@/components/common/Pagination";
import Modal from "@/components/common/Modal";
import EventDetailModal from "./EventDetailDrawer";
import EventActionModal from "./EventApprovalModal";
import EventSponsorshipModal from "./EventSponsorshipModal";
import EventRouteModal from "./EventRouteModal";
import { toast } from "react-toastify";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "sponsor_listed", label: "Sponsor Listed" },
  { value: "published", label: "Published" },
  { value: "hidden", label: "Hidden" },
  { value: "archived", label: "Archived" },
];

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "cycle_tour", label: "Cycle Tour" },
  { value: "cycle_kumbh", label: "Cycle Kumbh" },
  { value: "flash_mob", label: "Flash Mob" },
  { value: "koset_ride", label: "Koset Ride" },
];

const STATUS_BADGE_CONFIG = {
  submitted: { tone: "info", label: "Submitted", icon: <Clock className="w-3 h-3" /> },
  sponsor_listed: { tone: "organiser", label: "Sponsor Listed", icon: <ListChecks className="w-3 h-3" /> },
  published: { tone: "success", label: "Published", icon: <Globe className="w-3 h-3" /> },
  hidden: { tone: "warning", label: "Hidden", icon: <EyeOff className="w-3 h-3" /> },
  archived: { tone: "muted", label: "Archived", icon: <Archive className="w-3 h-3" /> },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatEventType = (type) => {
  if (!type) return "—";
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const EventsList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    events, totalEvents,
    eventsPage, eventsTotalPages, eventsLimit,
    componentCounts,
    isLoadingEvents, isUpdating,
    auditNavEntity, isLoadingAuditNav,
  } = useSelector((state) => state.admin);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    event_type: "",
    component: "",
    city: "",
    startDate: "",
    endDate: "",
    organizer: "",
    sort: "recent"
  });

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'details', 'sponsorship', 'route', 'moderation', 'menu'

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // ─── Audit Log Deep-Link: auto-open modal when ?id= is in URL ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkId = params.get('id');
    if (deepLinkId) {
      // Strip the param from URL immediately so back/forward doesn't re-trigger
      navigate(location.pathname, { replace: true });
      dispatch(fetchAdminEventById(deepLinkId));
    }
    return () => { dispatch(clearAuditNav()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When auditNavEntity is set (fetch resolved), open the details modal
  useEffect(() => {
    if (auditNavEntity && auditNavEntity._id) {
      setSelectedEvent(auditNavEntity);
      setActiveModal('details');
    }
  }, [auditNavEntity]);

  // Fetch events on mount and when filters change
  const fetchEvents = React.useCallback(
    (page = 1) => {
      const params = {
        page,
        limit: eventsLimit,
        ...filters
      };
      
      // Remove empty strings
      Object.keys(params).forEach(key => {
        if (params[key] === "") {
          delete params[key];
        }
      });

      dispatch(fetchAdminEvents(params));
    },
    [dispatch, eventsLimit, filters]
  );

  useEffect(() => {
    fetchEvents(1);
  }, [fetchEvents]);

  const handlePageChange = (newPage) => {
    fetchEvents(newPage);
  };

  const handleOpenModal = (event, modalType) => {
    setSelectedEvent(event);
    setActiveModal(modalType);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedEvent(null);
  };

  const handleActionComplete = () => {
    handleCloseModal();
    // Refetch to get fresh data, stay on current page
    fetchEvents(eventsPage);
  };

  const getStatusBadge = (status) => {
    const config = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.submitted;
    return (
      <Badge tone={config.tone}>
        <span className="flex items-center gap-1">{config.icon} {config.label}</span>
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Events Management
        </h2>
        <span className="text-sm text-gray-500">
          {events.length} of {totalEvents} event{totalEvents !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Filters */}
      <EventFilters
         filters={filters}
         onChange={handleFilterChange}
         componentCounts={componentCounts}
         statusOptions={STATUS_OPTIONS}
         extraFilters={
           <div className="flex-1 min-w-[150px]">
             <input
               type="text"
               placeholder="Organizer Username..."
               value={filters.organizer || ""}
               onChange={(e) => handleFilterChange("organizer", e.target.value)}
               className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
             />
           </div>
         }
      />

      {/* Table */}
      {isLoadingEvents ? (
        <div className="py-12 text-center text-gray-500">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="py-12 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No events found matching your filters</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Organizer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {events.map((event) => (
                  <motion.tr
                    key={event._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800 dark:text-gray-100 truncate max-w-[200px]">
                        {event.title}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {formatEventType(event.event_type)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {event.location?.city || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(event.start_date)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-800 dark:text-gray-100">
                        {event.organizer_id?.organization_name || "—"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {event.organizer_id?.email || "—"}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(event.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleOpenModal(event, 'menu')}
                          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination
        page={eventsPage}
        totalPages={eventsTotalPages}
        onChange={handlePageChange}
      />

      {activeModal === 'menu' && selectedEvent && (
        <Modal
          open={true}
          onClose={handleCloseModal}
          title={`Actions: ${selectedEvent.title}`}
          maxWidth="max-w-md"
        >
          <div className="grid gap-3 py-2">
            <button
              onClick={() => handleOpenModal(selectedEvent, 'details')}
              className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors group"
            >
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40">
                <ListChecks className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-gray-800 dark:text-gray-100">View Details</p>
                <p className="text-xs text-gray-500">Full event information and audit trail</p>
              </div>
            </button>

            <button
               onClick={() => handleOpenModal(selectedEvent, 'sponsorship')}
               className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors group"
            >
              <div className="p-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg group-hover:bg-green-100 dark:group-hover:bg-green-900/40">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                 <p className="font-medium text-gray-800 dark:text-gray-100">Sponsorship Management</p>
                 <p className="text-xs text-gray-500">Manage sponsors and interests</p>
              </div>
            </button>

            <button
               onClick={() => handleOpenModal(selectedEvent, 'route')}
               className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors group"
            >
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg group-hover:bg-purple-100 dark:group-hover:bg-purple-900/40">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                 <p className="font-medium text-gray-800 dark:text-gray-100">Route Details</p>
                 <p className="text-xs text-gray-500">Map, checkpoints, and GPX data</p>
              </div>
            </button>

            <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

            <button
               onClick={() => handleOpenModal(selectedEvent, 'moderation')}
               className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors group"
            >
              <div className="p-2 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-lg group-hover:bg-orange-100 dark:group-hover:bg-orange-900/40">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                 <p className="font-medium text-gray-800 dark:text-gray-100">Event Moderation</p>
                 <p className="text-xs text-gray-500">Approve, reject, highlight, or archive</p>
              </div>
            </button>
          </div>
        </Modal>
      )}

      {activeModal === 'details' && selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onOpenActions={() => setActiveModal('moderation')}
        />
      )}

      {activeModal === 'sponsorship' && selectedEvent && (
        <EventSponsorshipModal
          event={selectedEvent}
          onClose={handleCloseModal}
        />
      )}

      {activeModal === 'route' && selectedEvent && (
        <EventRouteModal
           event={selectedEvent}
           onClose={handleCloseModal}
        />
      )}

      {activeModal === 'moderation' && selectedEvent && (
        <EventActionModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onActionComplete={handleActionComplete}
        />
      )}
    </div>
  );
};

export default EventsList;
